import { useState, useEffect } from 'react';
import { Field, Formik } from 'formik';
import { StepBlock } from '../../../components/StepBlock/StepBlock';
import { Button } from '../../../components/Button/Button';
import plus from 'assets/images/icons/plus.svg';
import arrowNext from 'assets/images/icons/arrow-next.svg';
import successIcon from 'assets/images/icons/success.svg';
import checkIcon from 'assets/images/icons/check.svg';
import trashIcon from 'assets/images/icons/trash.svg';
import { Datalist } from 'components/Datalist/Datalist';
import { CreatePersonPayload, InformativeReunionStatus, Person } from 'services/person/person.types';
import { AddressAutocomplete } from 'components/Address/Address';
import React from 'react';
import { useSelector } from 'react-redux';
import { getDatalists } from 'features/datalist';
import { createNewPersonFirstStepSchema } from '../NewPersonFormsSchema';
import TipTap from 'components/TipTap/TipTap';
import { MultiSelect } from 'components/MultiSelect/MultiSelect';
import Select from 'react-select';
import { User } from 'types/User';
import FormErrorMessage from 'components/FormErrorMessage/FormErrorMessage';

const FormikDebug: React.FC<{ values: any }> = ({ values }) => {
    useEffect(() => {}, [values]);

    return null;
};

interface NewPersonFirstStepProps {
    isQuickAdd: boolean;
    setIsQuickAdd: (value: React.SetStateAction<boolean>) => void;
    handleNextStep: (data: CreatePersonPayload) => void;
    handleQuickCreatePerson: (data: CreatePersonPayload) => Promise<void>;
    isSuccess: boolean;
    person: Person;
    users: User[];
}

export const NewPersonFirstStep: React.FC<NewPersonFirstStepProps> = ({
    isQuickAdd,
    setIsQuickAdd,
    handleQuickCreatePerson,
    isSuccess,
    handleNextStep,
    person,
    users
}) => {
    const [sameAddressMap, setSameAddressMap] = useState<Record<number, boolean>>({ 0: true });
    const { datalists } = useSelector(getDatalists);

    return (
        <>
            <Formik
                initialValues={{
                    firstname: person?.firstname || '',
                    lastname: person?.lastname || '',
                    born_date: person?.born_date || '',
                    first_contact_date: person?.first_contact_date || '',
                    phone: person?.phone || '',
                    departments: person?.departments || [],
                    genre: person?.genre || '',
                    email: person?.email || '',
                    address: person?.address || {
                        address: '',
                        address_add: '',
                        city: '',
                        zipcode: '',
                        country: ''
                    },
                    informative_reunion: person?.informative_reunion || {
                        done: false,
                        date: '',
                        location: '',
                        status: InformativeReunionStatus.PENDING,
                        supervisor: undefined,
                        appv: undefined
                    },
                    observation: person?.observation || '',
                    source: person?.source || '',
                    initial_request: person?.initial_request || '',
                    stakeholders: person?.stakeholders || [
                        {
                            main_contact: true,
                            legal_contact: false,
                            firstname: '',
                            lastname: '',
                            phone: '',
                            email: '',
                            status: '',
                            address: {
                                address: '',
                                address_add: '',
                                city: '',
                                zipcode: '',
                                country: ''
                            },
                            observation: ''
                        }
                    ],
                    send_access_link: false
                }}
                validationSchema={() => createNewPersonFirstStepSchema(datalists || {})}
                onSubmit={async (values: CreatePersonPayload, { setSubmitting }) => {
                    if (
                        values.informative_reunion?.status !== InformativeReunionStatus.PENDING &&
                        values.responsible_person &&
                        !isQuickAdd
                    ) {
                        delete values.responsible_person;
                    }
                    isQuickAdd ? await handleQuickCreatePerson(values) : handleNextStep(values);
                    setSubmitting(false);
                }}
            >
                {({ values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
                    const hasMainContact = values.stakeholders?.some((s) => s.main_contact === true);

                    return (
                        <form onSubmit={handleSubmit} className="form">
                            <FormikDebug values={values} />
                            <StepBlock title="Personne soutenue">
                                <div className="form-line">
                                    <div className="form-group">
                                        <label>Prénom*</label>
                                        <input
                                            type="text"
                                            name="firstname"
                                            placeholder="Maxime"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.firstname}
                                            required
                                        />
                                        {errors.firstname && touched.firstname && <p className="error">{errors.firstname}</p>}
                                    </div>

                                    <div className="form-group">
                                        <label>Nom*</label>
                                        <input
                                            className="uppercase"
                                            type="text"
                                            name="lastname"
                                            placeholder="Navarro"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.lastname}
                                            required
                                        />
                                        {errors.lastname && touched.lastname && <p className="error">{errors.lastname}</p>}
                                    </div>

                                    <div className="form-group">
                                        <label>Numéro de téléphone*</label>
                                        <input
                                            type="text"
                                            name="phone"
                                            placeholder="06 06 06 06 06"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.phone}
                                            required
                                        />
                                        {errors.phone && touched.phone && <p className="error">{errors.phone}</p>}
                                    </div>
                                    <div className="form-group">
                                        <label>Département*</label>
                                        <MultiSelect
                                            name="departments"
                                            isMulti={false}
                                            placeholder="Sélectionner le département"
                                            values={
                                                values.departments && values.departments.length > 0 && values.departments[0]
                                                    ? {
                                                          label: datalists?.departments?.[values.departments[0]] || values.departments[0],
                                                          value: values.departments[0]
                                                      }
                                                    : null
                                            }
                                            update={(e: { label: string; value: string } | null) => {
                                                const validDepartments = e && e.value ? [e.value] : [];
                                                setFieldValue('departments', validDepartments);
                                            }}
                                            labelKey="label"
                                            valueKey="value"
                                            options={Object.entries(datalists?.departments || {}).map(([code, label]) => ({
                                                label: label,
                                                value: code
                                            }))}
                                            colors={[]}
                                        />
                                        {errors.departments && touched.departments && <p className="error">{errors.departments}</p>}
                                    </div>
                                </div>

                                <div className="form-line">
                                    <div className="form-group">
                                        <div className="checkbox">
                                            <label className="checkbox">
                                                <input
                                                    type="checkbox"
                                                    name="full_add"
                                                    checked={!isQuickAdd}
                                                    onChange={handleChange}
                                                    value={values.full_add}
                                                    onClick={() => setIsQuickAdd((prevState) => !prevState)}
                                                />
                                                Remplir le profil complet
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {!isQuickAdd && (
                                    <>
                                        <div className="form-line">
                                            <div className="form-group">
                                                <label>Genre</label>
                                                <Datalist
                                                    list="person_genres"
                                                    name="genre"
                                                    placeholder="Sélectionner un genre"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.genre}
                                                />
                                            </div>

                                            <div className="form-group">
                                                <label>Date de naissance</label>
                                                <input
                                                    type="date"
                                                    name="born_date"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.born_date}
                                                />
                                                {errors.born_date && <p className="error">{errors.born_date}</p>}
                                            </div>
                                        </div>

                                        <div className="form-line">
                                            <div className="form-group">
                                                <label>Date du premier contact</label>
                                                <input
                                                    type="date"
                                                    name="first_contact_date"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.first_contact_date}
                                                />
                                                {errors.first_contact_date && <p className="error">{errors.first_contact_date}</p>}
                                            </div>

                                            <div className="form-group">
                                                <label>Adresse e-mail</label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    placeholder="maxime.navarro@gmail.com"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.email}
                                                />
                                                {errors.email && <p className="error">{errors.email}</p>}
                                            </div>
                                        </div>

                                        <div className="form-line">
                                            <div className="form-group">
                                                <label>Adresse postale</label>
                                                <div className="fields address">
                                                    <AddressAutocomplete
                                                        baseField="address"
                                                        formValue={values.address}
                                                        setValue={setFieldValue}
                                                    />
                                                </div>
                                                {errors.address && typeof errors.address === 'string' && (
                                                    <p className="error">{errors.address}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="form-line">
                                            <div className="form-group">
                                                <label>Source du contact</label>
                                                <Datalist
                                                    list="person_sources"
                                                    name="source"
                                                    placeholder="Comment a-t-elle découvert le DAPV?"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.source}
                                                />
                                                {errors.source && <p className="error">{errors.source}</p>}
                                            </div>
                                        </div>

                                        <div className="form-line">
                                            <div className="form-group">
                                                <label>Commentaire</label>
                                                <div className="wysiwyg-container">
                                                    <TipTap
                                                        name="observation"
                                                        value={values.observation}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                    />
                                                </div>
                                                {errors.observation && <p className="error">{errors.observation}</p>}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </StepBlock>
                            {!isQuickAdd ? (
                                <>
                                    <StepBlock title="Rencontre informative">
                                        <div className="form-line">
                                            <div className="form-group">
                                                <label className="checkbox">
                                                    <Field type="checkbox" name="informative_reunion.done" />
                                                    Réunion informative réalisée
                                                </label>
                                                <FormErrorMessage field={`informative_reunion.done`} />
                                            </div>
                                        </div>
                                        {values.informative_reunion?.done && (
                                            <>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <label>Date de la réunion</label>
                                                        <input
                                                            type="date"
                                                            name="informative_reunion.date"
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            value={values.informative_reunion?.date || ''}
                                                        />
                                                        <FormErrorMessage field={`informative_reunion.date`} />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Intervenant ayant realisé la reunion</label>
                                                        <Select
                                                            options={users.map((user) => ({
                                                                value: user.id,
                                                                label: user.fullname
                                                            }))}
                                                            placeholder="Choisir l'intervenant ayant réalisé la réunion"
                                                            styles={{
                                                                input: (base) => {
                                                                    return {
                                                                        ...base,
                                                                        display: 'flex',
                                                                        alignItems: 'center'
                                                                    };
                                                                }
                                                            }}
                                                            defaultValue={
                                                                person.informative_reunion?.supervisor
                                                                    ? {
                                                                          value: person.informative_reunion?.supervisor?.id,
                                                                          label: person.informative_reunion?.supervisor?.fullname
                                                                      }
                                                                    : {
                                                                          value: null,
                                                                          label: 'Choisir un intervenant'
                                                                      }
                                                            }
                                                            onChange={(val) => {
                                                                setFieldValue(
                                                                    'informative_reunion.supervisor',
                                                                    users.find((user) => user.id === val?.value)
                                                                );
                                                            }}
                                                        />
                                                        <FormErrorMessage field={`informative_reunion.supervisor`} />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <label>Lieu de la réunion</label>
                                                        <input
                                                            type="text"
                                                            name="informative_reunion.location"
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            value={values.informative_reunion.location || ''}
                                                        />
                                                        <FormErrorMessage field={`informative_reunion.location`} />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Département</label>
                                                        <Datalist
                                                            list="departments"
                                                            name="informative_reunion.department"
                                                            placeholder="Département de la rencontre ?"
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            value={values.informative_reunion.department}
                                                        />
                                                        <FormErrorMessage field={`informative_reunion.department`} />
                                                    </div>
                                                </div>
                                                <div className="form-line">
                                                    <div className="form-group">
                                                        <label htmlFor="">Status de la personne à l'issue de la réunion</label>
                                                        <div className="form-line">
                                                            <label className="checkbox">
                                                                <Field
                                                                    type="radio"
                                                                    name="informative_reunion.status"
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    value={InformativeReunionStatus.PENDING}
                                                                />
                                                                Entrée en attente
                                                            </label>
                                                            <label className="checkbox">
                                                                <Field
                                                                    type="radio"
                                                                    name="informative_reunion.status"
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    value={InformativeReunionStatus.WAITING}
                                                                />
                                                                Souhaite réfléchir
                                                            </label>
                                                            <label className="checkbox">
                                                                <Field
                                                                    type="radio"
                                                                    name="informative_reunion.status"
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    value={InformativeReunionStatus.NO_FOLLOWUP}
                                                                />
                                                                Sans suite
                                                            </label>
                                                        </div>
                                                        <FormErrorMessage field={`informative_reunion.status`} />
                                                    </div>
                                                </div>
                                                {values.informative_reunion.status === InformativeReunionStatus.PENDING && (
                                                    <div className="form-line">
                                                        <div className="form-group">
                                                            <label>APPV Attribué</label>
                                                            <Select
                                                                options={users.map((user) => ({
                                                                    value: user.id,
                                                                    label: user.fullname
                                                                }))}
                                                                placeholder="Sélectionnez un APPV"
                                                                styles={{
                                                                    input: (base) => {
                                                                        return {
                                                                            ...base,
                                                                            display: 'flex',
                                                                            alignItems: 'center'
                                                                        };
                                                                    }
                                                                }}
                                                                defaultValue={
                                                                    person.responsible_person
                                                                        ? {
                                                                              value: person.responsible_person?.id,
                                                                              label: person.responsible_person?.fullname
                                                                          }
                                                                        : {
                                                                              value: null,
                                                                              label: 'Choisir un APPV'
                                                                          }
                                                                }
                                                                onChange={(val) => {
                                                                    setFieldValue(
                                                                        'responsible_person',
                                                                        users.find((user) => user.id === val?.value)
                                                                    );
                                                                }}
                                                            />
                                                            <FormErrorMessage field={`responsible_person`} />
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </StepBlock>
                                    <StepBlock title="Projet de vie">
                                        <div className="form-line">
                                            <div className="form-group">
                                                <label>Nom</label>
                                                <input
                                                    type="text"
                                                    name="label"
                                                    placeholder="Nom du projet de vie"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={values.label}
                                                />
                                                {errors.label && <p className="error">{errors.label}</p>}
                                            </div>
                                        </div>
                                        <div className="form-line">
                                            <div className="form-group">
                                                <label>Description</label>
                                                <div className="wysiwyg-container">
                                                    <TipTap
                                                        name="initial_request"
                                                        value={values.initial_request}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                    />
                                                </div>
                                                {errors.initial_request && <p className="error">{errors.initial_request}</p>}
                                            </div>
                                        </div>
                                    </StepBlock>
                                    {values.stakeholders &&
                                        values.stakeholders?.map((stakeholder, index) => (
                                            <React.Fragment key={`stakeholder-${index}`}>
                                                <StepBlock title={`Proche ${index + 1}`}>
                                                    <div className="form-line">
                                                        <div className="form-group">
                                                            <div className="checkbox">
                                                                <label className="checkbox">
                                                                    <input
                                                                        type="checkbox"
                                                                        name={`stakeholders.${index}.main_contact`}
                                                                        checked={stakeholder.main_contact === true}
                                                                        disabled={!stakeholder.main_contact && hasMainContact}
                                                                        onChange={() => {
                                                                            if (!stakeholder.main_contact && hasMainContact) {
                                                                                return;
                                                                            }
                                                                            handleChange({
                                                                                target: {
                                                                                    name: `stakeholders.${index}.main_contact`,
                                                                                    value: !stakeholder.main_contact
                                                                                }
                                                                            });
                                                                        }}
                                                                    />
                                                                    Contact principal
                                                                </label>
                                                                <label className="checkbox">
                                                                    <input
                                                                        type="checkbox"
                                                                        name={`stakeholders.${index}.legal_contact`}
                                                                        checked={stakeholder.legal_contact === true}
                                                                        onChange={() => {
                                                                            handleChange({
                                                                                target: {
                                                                                    name: `stakeholders.${index}.legal_contact`,
                                                                                    value: !stakeholder.legal_contact
                                                                                }
                                                                            });
                                                                        }}
                                                                    />
                                                                    Représentant légal
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="form-line">
                                                        <div className="form-group">
                                                            <label>Prénom</label>
                                                            <input
                                                                type="text"
                                                                name={`stakeholders.${index}.firstname`}
                                                                placeholder="Prénom du proche"
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                value={stakeholder.firstname}
                                                            />
                                                        </div>

                                                        <div className="form-group">
                                                            <label>Nom</label>
                                                            <input
                                                                type="text"
                                                                className="uppercase"
                                                                name={`stakeholders.${index}.lastname`}
                                                                placeholder="Nom du proche"
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                value={stakeholder.lastname}
                                                            />
                                                        </div>

                                                        <div className="form-group">
                                                            <label>Statut</label>
                                                            <Datalist
                                                                list="stakeholder_status"
                                                                name={`stakeholders.${index}.status`}
                                                                placeholder="Sélectionner un statut"
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                value={stakeholder.status}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-line">
                                                        <div className="form-group">
                                                            <label>Numéro de téléphone</label>
                                                            <input
                                                                type="text"
                                                                name={`stakeholders.${index}.phone`}
                                                                placeholder="Numéro de téléphone du proche"
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                value={stakeholder.phone}
                                                            />
                                                            {(errors.stakeholders as any)?.[index]?.phone && (
                                                                <p className="error">{(errors.stakeholders as any)?.[index]?.phone}</p>
                                                            )}
                                                        </div>

                                                        <div className="form-group">
                                                            <label>Adresse e-mail</label>
                                                            <input
                                                                type="email"
                                                                name={`stakeholders.${index}.email`}
                                                                placeholder="E-mail du proche"
                                                                onChange={handleChange}
                                                                onBlur={handleBlur}
                                                                value={stakeholder.email}
                                                            />
                                                            {(errors.stakeholders as any)?.[index]?.email && (
                                                                <p className="error">{(errors.stakeholders as any)?.[index]?.email}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="form-line">
                                                        <div className="form-group">
                                                            <label>Adresse postale</label>
                                                            <div className="radios">
                                                                <label className="radio">
                                                                    <input
                                                                        type="radio"
                                                                        checked={sameAddressMap[index] === true}
                                                                        onChange={() => {
                                                                            setSameAddressMap((prev) => ({
                                                                                ...prev,
                                                                                [index]: true
                                                                            }));
                                                                        }}
                                                                    />
                                                                    Identique à la personne soutenue
                                                                </label>
                                                                <label className="radio">
                                                                    <input
                                                                        type="radio"
                                                                        checked={sameAddressMap[index] === false}
                                                                        onChange={() => {
                                                                            setSameAddressMap((prev) => ({
                                                                                ...prev,
                                                                                [index]: false
                                                                            }));
                                                                        }}
                                                                    />
                                                                    Autre adresse
                                                                </label>
                                                            </div>
                                                            {!sameAddressMap[index] && (
                                                                <>
                                                                    <div className="fields address">
                                                                        <AddressAutocomplete
                                                                            baseField={`stakeholders.${index}.address`}
                                                                            formValue={values.stakeholders?.[index]?.address}
                                                                            setValue={(field, value, shouldValidate) => {
                                                                                return setFieldValue(field, value, shouldValidate);
                                                                            }}
                                                                            id={`stakeholder-address-${index}`}
                                                                        />
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="form-line">
                                                        <div className="form-group">
                                                            <label>Commentaire</label>
                                                            <div className="wysiwyg-container">
                                                                <TipTap
                                                                    name={`stakeholders.${index}.observation`}
                                                                    value={stakeholder.observation}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {index > 0 && (
                                                        <Button
                                                            type="button"
                                                            variant="light"
                                                            label="Supprimer cette personne"
                                                            icon={<img src={trashIcon} alt="Supprimer cette personne" />}
                                                            iconBefore
                                                            onClick={() => {
                                                                const newStakeholders =
                                                                    values.stakeholders?.filter((_, i) => i !== index) || [];
                                                                setFieldValue('stakeholders', newStakeholders);
                                                            }}
                                                        />
                                                    )}
                                                </StepBlock>
                                            </React.Fragment>
                                        ))}
                                    <div
                                        className="button light"
                                        onClick={() => {
                                            const newStakeholder = {
                                                id: Date.now(),
                                                main_contact: !hasMainContact,
                                                legal_contact: false,
                                                firstname: '',
                                                lastname: '',
                                                fullname: '',
                                                phone: '',
                                                email: '',
                                                status: '',
                                                address: {
                                                    address: '',
                                                    address_add: '',
                                                    city: '',
                                                    zipcode: '',
                                                    country: ''
                                                },
                                                observation: ''
                                            };

                                            handleChange({
                                                target: {
                                                    name: 'stakeholders',
                                                    value: [...(values.stakeholders || []), newStakeholder]
                                                }
                                            });
                                            setSameAddressMap((prev) => ({
                                                ...prev,
                                                [(values.stakeholders || []).length]: false
                                            }));
                                        }}
                                    >
                                        <img src={plus} alt="Ajouter un proche" />
                                        Ajouter un proche
                                    </div>
                                    <Button
                                        type="submit"
                                        label="Passer à l'étape suivante"
                                        icon={<img src={arrowNext} alt="Passer à l'étape suivante" />}
                                        iconAfter
                                    />
                                </>
                            ) : (
                                <>
                                    <Button
                                        type="submit"
                                        label="Enregistrer le dossier"
                                        icon={<img src={checkIcon} alt="Enregistrer le dossier" />}
                                        iconAfter
                                    />
                                </>
                            )}
                        </form>
                    );
                }}
            </Formik>
            {isSuccess && (
                <div className="success">
                    <div className="success-block">
                        <p>Votre dossier est bien enregistré</p>
                        <img src={successIcon} alt="Dossier enregistré avec succès" />
                    </div>
                </div>
            )}
        </>
    );
};
